import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EntityCommentableType } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';

export interface CommentableRef {
  commentableType: EntityCommentableType;
  commentableId: string;
}

const MAX_NOTES = 200;

/**
 * The newest notes on one record, newest first, polled so teammates' notes
 * show up without a reload.
 */
export function useEntityComments(ref: CommentableRef) {
  return useQuery({
    ...orpc.comments.list.queryOptions({
      input: { ...ref, limit: MAX_NOTES, offset: 0, order: 'desc' },
    }),
    refetchInterval: 30_000,
  });
}

export function useCreateEntityComment(ref: CommentableRef) {
  const queryClient = useQueryClient();
  return useMutation({
    ...orpc.comments.create.mutationOptions(),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: orpc.comments.list.key({ input: ref }),
      }),
  });
}

/** Edits the caller's own note; the server rejects anyone else's. */
export function useUpdateEntityComment(ref: CommentableRef) {
  const queryClient = useQueryClient();
  return useMutation({
    ...orpc.comments.update.mutationOptions(),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: orpc.comments.list.key({ input: ref }),
      }),
  });
}

/** People who can read the record, and so can be @mentioned on it. */
export function useMentionableUsers(ref: CommentableRef) {
  return useQuery({
    ...orpc.comments.listMentionableUsers.queryOptions({ input: ref }),
    staleTime: 5 * 60 * 1000,
  });
}
